/**
 * Администраторы — разметка 1:1 с legacy htdocs/core/templates/admin_users.tpl:
 * .bs-callout, form.form-settings, table.table.table-hover.table-bordered.table-condensed
 * с колонками «Имя пользователя / Изменить пароль / Суперпользователь», строка добавления
 * и кнопка «сохранить изменения». Кнопка удаления у id = 1 заблокирована — как в legacy.
 *
 * Legacy отправлял одну POST-форму: repass[id] (смена пароля), upd_admin_value[id]
 * (флаг суперадмина при settings-submit), module-delete[id] (удаление), name/password/
 * superuser (добавление). Здесь то же самое через POST/PUT/DELETE
 * /api/admin/system/users (Handlers\Admin\System, только суперадмин).
 */

import { useEffect, useState } from 'react'

import { api, ApiError } from '@/api/client'
import { isTrue } from '@/lib/format'
import { useLegacyCss } from '@/lib/legacyAssets'
import { useApi } from '@/lib/useApi'

/** Строка таблицы admin (без хеша пароля — бекенд его не отдаёт). */
interface AdminUser {
  id: number
  login: string
  superadmin: boolean | 't' | 'f'
}

export function AdminUsersPage() {
  useLegacyCss('admin', 'newapproach', 'docs.min')

  const { data, reload } = useApi<AdminUser[]>('/api/admin/system/users')

  const [superadmin, setSuperadmin] = useState<Record<number, boolean>>({})
  const [passwords, setPasswords] = useState<Record<number, string>>({})
  const [add, setAdd] = useState({ name: '', password: '', superuser: false })
  const [ok, setOk] = useState<string[]>([])
  const [error, setError] = useState<string[]>([])

  const users = data ?? []

  useEffect(() => {
    const flags: Record<number, boolean> = {}
    for (const user of users) flags[user.id] = isTrue(user.superadmin)
    setSuperadmin(flags)
    setPasswords({})
  }, [data])

  /** legacy settings-submit: сохраняет флаги суперадмина и добавляет пользователя. */
  const submit = async (): Promise<void> => {
    try {
      for (const user of users) {
        const flag = superadmin[user.id]
        if (flag === undefined) continue
        await api.put(`/api/admin/system/users/${user.id}`, { superadmin: flag })
      }
      if (add.name !== '' || add.password !== '') {
        await api.post('/api/admin/system/users', add)
        setAdd({ name: '', password: '', superuser: false })
      }
      setOk(['Изменения существующих настроек сохранены.'])
      setError([])
      reload()
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при сохранении.'])
      setOk([])
    }
  }

  /** legacy repass[id]. */
  const changePassword = async (id: number): Promise<void> => {
    try {
      const result = await api.post<{ message?: string }>(
        `/api/admin/system/users/${id}/password`,
        { password: passwords[id] ?? '' },
      )
      setOk(result.message === undefined ? [] : [result.message])
      setError([])
      setPasswords({ ...passwords, [id]: '' })
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при смене пароля.'])
      setOk([])
    }
  }

  /** legacy module-delete[id]. */
  const remove = async (id: number): Promise<void> => {
    try {
      const result = await api.delete<{ message: string }>(`/api/admin/system/users/${id}`)
      setOk([result.message])
      setError([])
      reload()
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при удалении.'])
      setOk([])
    }
  }

  return (
    <>
      {error.length > 0 && (
        <div className="bs-callout bs-callout-danger">
          <h4>Ошибка</h4>
          {error.map((message, index) => (
            <p key={index}>{message}</p>
          ))}
        </div>
      )}
      {ok.length > 0 && (
        <div className="bs-callout bs-callout-info">
          <h4>OK</h4>
          {ok.map((message, index) => (
            <p key={index}>{message}</p>
          ))}
        </div>
      )}

      <form
        className="form-settings"
        method="POST"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        <table className="table table-hover table-bordered table-condensed">
          <thead>
            <tr>
              <th>№</th>
              <th>Имя пользователя</th>
              <th>Изменить пароль</th>
              <th>Суперпользователь</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map((user, index) => (
              <tr key={user.id}>
                <td>{index + 1}</td>
                <td>
                  {user.login}
                  <input type="hidden" name={`uid[${user.id}]`} value={user.id} />
                </td>
                <td>
                  <input
                    placeholder="Новый пароль"
                    className="form-control"
                    style={{ width: 'auto', display: 'inline-block' }}
                    type="text"
                    name={`new_password[${user.id}]`}
                    value={passwords[user.id] ?? ''}
                    onChange={(e) => setPasswords({ ...passwords, [user.id]: e.target.value })}
                  />
                  <input
                    className="btn btn-xs btn-success"
                    type="submit"
                    name={`repass[${user.id}]`}
                    value="Изменить пароль"
                    onClick={(e) => {
                      e.preventDefault()
                      void changePassword(user.id)
                    }}
                  />
                </td>
                <td style={{ textAlign: 'center' }}>
                  <input
                    checked={superadmin[user.id] === true}
                    type="checkbox"
                    name={`upd_admin_value[${user.id}]`}
                    value="t"
                    onChange={(e) => setSuperadmin({ ...superadmin, [user.id]: e.target.checked })}
                  />
                </td>
                <td>
                  <input
                    disabled={user.id === 1}
                    type="submit"
                    name={`module-delete[${user.id}]`}
                    className="btn btn-xs btn-danger"
                    value="удалить"
                    onClick={(e) => {
                      e.preventDefault()
                      void remove(user.id)
                    }}
                  />
                </td>
              </tr>
            ))}
            <tr>
              <td />
              <td>
                <input
                  className="form-control"
                  name="name"
                  value={add.name}
                  onChange={(e) => setAdd({ ...add, name: e.target.value })}
                />
              </td>
              <td>
                <input
                  className="form-control"
                  name="password"
                  value={add.password}
                  onChange={(e) => setAdd({ ...add, password: e.target.value })}
                />
              </td>
              <td style={{ textAlign: 'center' }}>
                <input
                  type="checkbox"
                  name="superuser"
                  value="t"
                  checked={add.superuser}
                  onChange={(e) => setAdd({ ...add, superuser: e.target.checked })}
                />
              </td>
              <td />
            </tr>
          </tbody>
        </table>
        <input
          type="submit"
          name="settings-submit"
          className="btn btn-xs btn-info"
          value="сохранить изменения"
        />
      </form>
    </>
  )
}
